<script setup lang="ts">
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import { computed, ref, watch } from "vue";
import { useHeicObjectUrl } from "@/components/MyStudents/Submissions/composables/useHeicObjectUrl";
import { useSubmissionFilePresign } from "@/components/MyStudents/Submissions/composables/useSubmissionFilePresign";
import FileIcon from "@/components/shared/icons/FileIcon.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import type { SubmissionPreviewModalProps } from "../../types";
import { formatDate, getSubmissionFileName, isDocxFile, isHeicFile, isImageFile, isPdfFile } from "../../utils";
import DocumentDocxView from "../DocumentView/DocumentDocxView.vue";
import DocumentPDFView from "../DocumentView/DocumentPDFView.vue";
import SubmissionDownloadButton from "../SubmissionDownloadButton/index.vue";
import { ZOOM_DEFAULT, ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from "./constants";
import SubmissionPreviewError from "./SubmissionPreviewError.vue";
import { dialogPt, resetButtonPt, zoomButtonPt } from "./style";

defineOptions({
  name: "submissionPreviewModal",
});

const props = withDefaults(defineProps<SubmissionPreviewModalProps>(), {
  file: undefined,
  submissionId: undefined,
  submittedBy: undefined,
  submittedAt: undefined,
});

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
}>();

const { mutateAsync: presignPreview, isPending } = useSubmissionFilePresign();

const fileUrl = ref<string | undefined>(undefined);
const zoom = ref<number>(ZOOM_DEFAULT);
const naturalWidth = ref<number | undefined>(undefined);

const isVisible = computed<boolean>({
  get: (): boolean => props.visible,
  set: (value: boolean): void => emit("update:visible", value),
});

const fileName = computed<string>(() => getSubmissionFileName(props.file));
const isImage = computed<boolean>(() => isImageFile(fileName.value));
const isPdf = computed<boolean>(() => isPdfFile(fileName.value));
const isDocx = computed<boolean>(() => isDocxFile(fileName.value));
const isHeic = computed<boolean>(() => isHeicFile(fileName.value));

const { objectUrl: heicUrl, hasFailed: hasHeicFailed } = useHeicObjectUrl(fileUrl, isHeic);

const imageSrc = computed<string | undefined>(() => (isHeic.value ? heicUrl.value : fileUrl.value));
const showsImage = computed<boolean>(() => isImage.value || isHeic.value);

const previewScale = computed<number>(() => zoom.value / 100);

const subLine = computed<string>(() => {
  const submitted = [props.submittedBy, formatDate(props.submittedAt)].filter(Boolean).join(" · ");
  return submitted ? `Submitted by ${submitted}` : "";
});

const canZoomIn = computed<boolean>(() => zoom.value < ZOOM_MAX);
const canZoomOut = computed<boolean>(() => zoom.value > ZOOM_MIN);

const canPreview = computed<boolean>(
  () => !!fileUrl.value && !hasHeicFailed.value && (showsImage.value || isPdf.value || isDocx.value),
);

// Only once loading has settled, so the toolbar does not flicker away and back while the
// URL is still being minted. Zoom and download act on a preview that is not there.
const hasPreviewFailed = computed<boolean>(() => !isPending.value && !canPreview.value);

const isImageLoading = computed<boolean>(
  () =>
    !isPending.value &&
    !!fileUrl.value &&
    showsImage.value &&
    !hasHeicFailed.value &&
    naturalWidth.value === undefined,
);

// Sized from the image's own dimensions so that 100% means actual size and the
// scroll container grows with the zoom level.
const imageStyle = computed<Record<string, string>>((): Record<string, string> => {
  if (!naturalWidth.value) {
    return {};
  }
  return { width: `${(naturalWidth.value * zoom.value) / 100}px` };
});

const zoomIn = (): void => {
  zoom.value = Math.min(zoom.value + ZOOM_STEP, ZOOM_MAX);
};

const zoomOut = (): void => {
  zoom.value = Math.max(zoom.value - ZOOM_STEP, ZOOM_MIN);
};

const resetZoom = (): void => {
  zoom.value = ZOOM_DEFAULT;
};

const handleZoomScroll = (event: WheelEvent): void => {
  if (!event.ctrlKey && !event.metaKey) {
    return;
  }
  event.preventDefault();
  if (event.deltaY < 0) {
    zoomIn();
  } else {
    zoomOut();
  }
};

const handleImageLoad = (event: Event): void => {
  naturalWidth.value = (event.target as HTMLImageElement).naturalWidth;
};

const handlePreviewError = (): void => {
  fileUrl.value = undefined;
};

const loadFile = async (): Promise<void> => {
  if (!props.file?.bucket || !props.file?.key) {
    return;
  }
  try {
    fileUrl.value = await presignPreview({
      bucket: props.file.bucket,
      key: props.file.key,
      submissionId: props.submissionId,
    });
  } catch {
    fileUrl.value = undefined;
  }
};

// A URL is minted per opening rather than held across them, because the backend
// records one access row for every mint.
watch(
  (): boolean => props.visible,
  async (visible: boolean): Promise<void> => {
    if (!visible) {
      fileUrl.value = undefined;
      naturalWidth.value = undefined;
      return;
    }
    zoom.value = ZOOM_DEFAULT;
    await loadFile();
  },
  { immediate: true },
);
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    dismissable-mask
    close-on-escape
    :pt="dialogPt"
    data-testid="my-students-submission-preview-modal"
  >
    <template #header>
      <div class="flex items-center gap-3 min-w-0">
        <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-light">
          <FileIcon class="h-5 w-5 fill-current text-red-700" />
        </span>
        <div class="flex flex-col min-w-0">
          <span
            class="font-semibold text-base text-indigo-base break-all"
            data-testid="my-students-submission-preview-modal-filename"
          >
            {{ fileName }}
          </span>
          <span
            v-if="subLine"
            class="text-xs font-normal text-gray-800"
            data-testid="my-students-submission-preview-modal-subline"
          >
            {{ subLine }}
          </span>
        </div>
      </div>
    </template>

    <div
      v-if="!hasPreviewFailed"
      class="flex items-center justify-end gap-2 border-b border-gray-175 px-4 py-2.5"
    >
      <div class="inline-flex h-8 items-center gap-1 rounded border border-gray-175 px-1">
        <Button
          type="button"
          :disabled="!canZoomOut"
          :pt="zoomButtonPt"
          aria-label="Zoom out"
          data-testid="my-students-submission-preview-modal-zoom-out"
          @click="zoomOut"
        >
          <span class="text-lg font-bold leading-none">&minus;</span>
        </Button>
        <span
          class="w-12 text-center text-xs font-bold text-indigo-base"
          data-testid="my-students-submission-preview-modal-zoom-level"
        >
          {{ zoom }}%
        </span>
        <Button
          type="button"
          :disabled="!canZoomIn"
          :pt="zoomButtonPt"
          aria-label="Zoom in"
          data-testid="my-students-submission-preview-modal-zoom-in"
          @click="zoomIn"
        >
          <span class="text-lg font-bold leading-none">+</span>
        </Button>
      </div>
      <Button
        type="button"
        label="Reset"
        :pt="resetButtonPt"
        data-testid="my-students-submission-preview-modal-zoom-reset"
        @click="resetZoom"
      />
    </div>

    <div
      class="flex flex-1 overflow-auto bg-blue-100 p-6"
      data-testid="my-students-submission-preview-modal-body"
      @wheel="handleZoomScroll"
    >
      <SkeletonLoader
        v-if="isPending || isImageLoading"
        height="h-72"
        data-testid="my-students-submission-preview-modal-loader"
      />
      <img
        v-if="!isPending && imageSrc && showsImage"
        v-show="!isImageLoading"
        :src="imageSrc"
        :alt="fileName"
        :style="imageStyle"
        class="max-w-none shrink-0 mx-auto self-start bg-white shadow-lg"
        data-testid="my-students-submission-preview-modal-image"
        @load="handleImageLoad"
        @error="handlePreviewError"
      />
      <DocumentPDFView
        v-else-if="!isPending && fileUrl && isPdf"
        :url="fileUrl"
        :scale="previewScale"
        :allow-download="false"
        class="shrink-0 mx-auto self-start"
        data-testid="my-students-submission-preview-modal-pdf"
        @error="handlePreviewError"
      />
      <DocumentDocxView
        v-else-if="!isPending && fileUrl && isDocx"
        :url="fileUrl"
        :scale="previewScale"
        class="shrink-0 mx-auto self-start"
        data-testid="my-students-submission-preview-modal-docx"
        @error="handlePreviewError"
      />
      <SubmissionPreviewError v-else-if="hasPreviewFailed" />
    </div>

    <template v-if="!hasPreviewFailed" #footer>
      <SubmissionDownloadButton
        :file="file"
        :submission-id="submissionId"
        :disabled="!fileUrl"
        data-testid="my-students-submission-preview-modal-download"
      />
    </template>
  </Dialog>
</template>
